/**
 * Artificial limit for storage.
 * @public
 */
export const artificialByteLimit = 5221908;
/**
 * Calculates the size already used in a Storage object (SessionStorage or LocalStorage)
 * @internal
 * @param storage - Instance of SessionStorage or LocalStorage
 * @returns Size in Bytes that is already used in the given storage
 */
function getCurrentStorageSize(storage) {
    let totalSize = 0;
    for (const key in storage) {
        if (!Object.prototype.hasOwnProperty.call(storage, key)) {
            continue;
        }
        // JS stores every character as 2 Bytes
        const itemLength = (storage[key].length + key.length) * 2;
        totalSize += itemLength;
    }
    return totalSize;
}
/**
 * Try to store a key-value pair in a Storage object (SessionStorage or LocalStorage)
 * @public
 * @remarks Returns true if the value did get stored and false if did not.
 * @param key - Key to identify the value
 * @param value - Value to store
 * @param storage - Storage object (SessionStorage or LocalStorage) where the value will get stored
 * @returns true if the value did get store or false if did not.
 */
export function setItem(key, value, storage) {
    const keyString = generateKeyName(key);
    const itemSize = (keyString.length + value.length) * 2;
    const currentSize = getCurrentStorageSize(storage);
    if (itemSize + currentSize < artificialByteLimit) {
        storage.setItem(keyString, value);
        return true;
    }
    return false;
}
/**
 * Gets a value from a store instance using a valid key
 * @public
 * @param key - Key to identify the value
 * @param storage - Storage object (SessionStorage or LocalStorage) from where the value will get read
 * @returns value from the store or null if the value does not exist
 */
export function getItem(key, storage) {
    const keyString = generateKeyName(key);
    return storage.getItem(keyString);
}
/**
 * Removes a value from a store instance using a valid key
 * @public
 * @param key - Key to identify the value
 * @param storage - Storage object (SessionStorage or LocalStorage) from where the value will get remove
 */
export function removeItem(key, storage) {
    const keyString = generateKeyName(key);
    storage.removeItem(keyString);
}
/**
 * Generates a valid key for the web-storage based on the provided identifiers
 * @public
 * @param key - Storage get object
 * @returns Valid web-storage key
 */
export function generateKeyName({ appId, dataId, uniqueId }) {
    return appId + '-' + dataId + (uniqueId ? '-' + uniqueId : '');
}
